// This module owns controller LED color generation and sync decisions.
// It reads the current live keyboard/controller state and produces device-
// specific color payloads for LinnStrument, Lumatone, and Exquis. It does not
// decide modulation or tuning; it only reflects the current interpreted surface.

import { HSVtoRGB2, nameToHex, hex2rgb, rgb2hsv, rgbToHex } from "./color_utils";
import {
  buildLinnstrumentDegreeMap,
  LINNS_OFF,
  LINNS_RED,
} from "../controllers/linnstrument-config.js";
import {
  transferColor,
  LUMATONE_TONIC,
  LUMATONE_TONIC_OTHER,
} from "../settings/scale/color-transfer.js";
import {
  degreeFilterSetFromSettings,
  LUMATONE_COLOR_FILTER_DARK,
} from "../controllers/lumatone-color-filters.js";
import { detectController } from "../controllers/registry.js";
import { loadAnchorSettingsUpdate } from "../input/controller-anchor.js";
import { modulatedControllerCoords } from "./modulation-controller-runtime.js";

const LUMATONE_BYPASS_ANCHOR_NOTE = 60;

function lumatoneBypassChannelCandidates(anchorChannel = 4) {
  const normalized = Math.max(1, Math.min(16, Math.trunc(anchorChannel || 4)));
  const candidates = [{ channel: normalized, offset: 0 }];
  for (let delta = 1; delta <= 15; delta += 1) {
    const lower = normalized - delta;
    const upper = normalized + delta;
    if (lower >= 1) candidates.push({ channel: lower, offset: -delta });
    if (upper <= 16) candidates.push({ channel: upper, offset: delta });
  }
  return candidates;
}

function controllerCoordsForDisplay(coords) {
  if (typeof this._modulatedControllerCoords === "function") {
    return this._modulatedControllerCoords(coords);
  }
  return modulatedControllerCoords(this, coords);
}

function selectedInputMatchesController(controllerId) {
  if ((this.settings.midiin_controller_override || "auto") === "auto") return true;
  const detected = detectController(this.midiin_data?.name?.toLowerCase() ?? "");
  return detected?.id === controllerId;
}

export function canAutoSendLumatoneColors() {
  return (
    this.controller?.id === "lumatone" &&
    selectedInputMatchesController.call(this, "lumatone") &&
    !this.settings.midi_passthrough &&
    this.inputRuntime?.target !== "scale" &&
    this.inputRuntime?.layoutMode !== "sequential" &&
    !!this.controllerMap
  );
}

export function canAutoSendExquisColors() {
  return (
    this.controller?.id === "exquis" &&
    selectedInputMatchesController.call(this, "exquis") &&
    !this.settings.midi_passthrough &&
    this.inputRuntime?.target !== "scale" &&
    this.inputRuntime?.layoutMode !== "sequential" &&
    !!this.controllerMap
  );
}

export function canAutoSendLinnstrumentColors() {
  return (
    this.controller?.id === "linnstrument" &&
    selectedInputMatchesController.call(this, "linnstrument") &&
    !this.settings.midi_passthrough &&
    this.inputRuntime?.target !== "scale" &&
    this.inputRuntime?.layoutMode !== "sequential" &&
    !!this.controllerMap
  );
}

export function updateColors(colors, options = {}) {
  this.settings.note_colors = colors.note_colors;
  this.settings.spectrum_colors = colors.spectrum_colors;
  this.settings.fundamental_color = colors.fundamental_color;

  if (options.preview) {
    this.scheduleColorPreviewRedraw(options.degreeIndex);
    return;
  }

  this.scheduleGridRedraw();

  // Controller LED queues collapse rapid color drags to the latest state.
  if (
    this.lumatoneLEDs &&
    this.settings.lumatone_led_sync &&
    canAutoSendLumatoneColors.call(this)
  ) {
    this.lumatoneLEDs.sendAll(this._buildLumatoneColorEntries());
  }

  if (this.exquisLEDs && this.settings.exquis_led_sync && canAutoSendExquisColors.call(this)) {
    this.exquisLEDs.sendColors(this._buildExquisColorArray());
  }

  if (
    this.linnstrumentLEDs &&
    this.settings.linnstrument_led_sync &&
    canAutoSendLinnstrumentColors.call(this)
  ) {
    this.linnstrumentLEDs.updatePaletteValues(this._buildLinnstrumentColorArray());
  }
}

export function syncLumatoneLEDs() {
  if (this.lumatoneLEDs && this.controller?.id === "lumatone" && this.controllerMap) {
    this.lumatoneLEDs.sendAll(this._buildLumatoneColorEntries());
  }
}

export function autoSyncLumatoneLEDs() {
  if (this.lumatoneLEDs && canAutoSendLumatoneColors.call(this)) {
    this.lumatoneLEDs.sendAll(this._buildLumatoneColorEntries());
  }
}

export function syncExquisLEDs() {
  if (this.exquisLEDs && canAutoSendExquisColors.call(this)) {
    this.exquisLEDs.sendColors(this._buildExquisColorArray());
  }
}

export function syncLinnstrumentLEDs() {
  if (this.linnstrumentLEDs && canAutoSendLinnstrumentColors.call(this)) {
    this.linnstrumentLEDs.sendPaletteValues(this._buildLinnstrumentColorArray());
  }
}

export function buildLinnstrumentColorArray() {
  const degreeColors = new Map();
  for (const [, coords] of this.controllerMap) {
    const mappedCoords = controllerCoordsForDisplay.call(this, coords);
    const [cents, reducedSteps] = this.hexCoordsToCents(mappedCoords);
    if (!degreeColors.has(reducedSteps)) {
      degreeColors.set(reducedSteps, this._getScreenHexColor(cents, reducedSteps));
    }
  }

  const degreeMap = buildLinnstrumentDegreeMap(degreeColors);
  const values = new Array(128).fill(LINNS_OFF);
  for (const [mapKey, coords] of this.controllerMap) {
    const dot = mapKey.indexOf(".");
    const ch = parseInt(mapKey.slice(0, dot), 10);
    const col = parseInt(mapKey.slice(dot + 1), 10);
    const note = (ch - 1) * 16 + (col - 1);
    if (note < 0 || note > 127) continue;
    const [, reducedSteps] = this.hexCoordsToCents(controllerCoordsForDisplay.call(this, coords));
    values[note] = reducedSteps === 0 ? LINNS_RED : (degreeMap.get(reducedSteps) ?? LINNS_OFF);
  }
  return values;
}

export function sendLumatoneLayout() {
  if (!this.lumatoneLEDs) return;

  const entries = [];
  for (let b = 1; b <= 5; b++) {
    for (let k = 0; k < 56; k++) {
      entries.push({
        board: b,
        key: k,
        note: k,
        channel: b - 1,
        keyType: 0x01,
        hexColor: "#000000",
      });
    }
  }

  this.lumatoneLEDs.sendLayout(entries, [{ cmd: 0x0e, board: 0, value: 1 }]);
}

function chooseLumatoneBypassAssignment(steps, centerDegree, equivSteps, anchorChannel) {
  for (const candidate of lumatoneBypassChannelCandidates(anchorChannel)) {
    const rawNote =
      LUMATONE_BYPASS_ANCHOR_NOTE + steps - centerDegree - candidate.offset * equivSteps;
    if (rawNote >= 0 && rawNote <= 127) {
      return {
        note: rawNote,
        channel: candidate.channel,
        exact: true,
      };
    }
  }

  return null;
}

export function buildLumatoneBypassLayoutEntries() {
  const layout2dSettings = this.settings?.midi_passthrough
    ? {
        ...this.settings,
        ...loadAnchorSettingsUpdate(this.controller, {
          ...this.settings,
          midi_passthrough: false,
        }),
        midi_passthrough: false,
      }
    : this.settings;
  const controllerMap =
    this.controllerMap ?? this._buildControllerMapForSettings?.(layout2dSettings);
  if (!controllerMap) return null;

  const entries = [];
  let exactCount = 0;
  let disabledCount = 0;
  const centerDegree = this.settings.center_degree || 0;
  const equivSteps = this.settings.equivSteps || this.tuning?.scale?.length || 1;
  const anchorChannel = this.settings.midiin_anchor_channel ?? 4;

  for (const [mapKey, coords] of controllerMap) {
    const dotIdx = mapKey.indexOf(".");
    const board = parseInt(mapKey.slice(0, dotIdx), 10);
    const key = parseInt(mapKey.slice(dotIdx + 1), 10);
    const mappedCoords = controllerCoordsForDisplay.call(this, coords);
    const [, , steps] = this.hexCoordsToCents(mappedCoords);
    const assignment = chooseLumatoneBypassAssignment(
      steps,
      centerDegree,
      equivSteps,
      anchorChannel,
    );

    if (assignment) {
      exactCount += 1;
      entries.push({
        board,
        key,
        note: assignment.note,
        channel: assignment.channel - 1,
        keyType: 0x01,
        hexColor: this._getLumatoneHexColor(mappedCoords),
      });
      continue;
    }

    disabledCount += 1;
    entries.push({
      board,
      key,
      note: 0,
      channel: 0,
      keyType: 0x10,
      hexColor: "#000000",
    });
  }

  entries.sort((a, b) => a.board - b.board || a.key - b.key);

  return {
    entries,
    exactCount,
    disabledCount,
    totalCount: entries.length,
  };
}

export function sendLumatoneBypassLayout() {
  if (!this.lumatoneLEDs) return null;
  const payload = buildLumatoneBypassLayoutEntries.call(this);
  if (!payload) return null;
  this.lumatoneLEDs.sendLayout(payload.entries, [{ cmd: 0x0e, board: 0, value: 1 }]);
  return payload;
}

export function buildLumatoneColorEntries() {
  const entries = [];
  const darkMode = this.settings?.lumatone_degree_filter_mode === LUMATONE_COLOR_FILTER_DARK;
  const degreeFilter = degreeFilterSetFromSettings(this.settings);
  for (const [mapKey, coords] of this.controllerMap) {
    const dotIdx = mapKey.indexOf(".");
    const board = parseInt(mapKey.slice(0, dotIdx), 10);
    const key = parseInt(mapKey.slice(dotIdx + 1), 10);
    const mappedCoords = controllerCoordsForDisplay.call(this, coords);
    const [, reducedSteps] = this.hexCoordsToCents(mappedCoords);
    const hexColor =
      darkMode || (degreeFilter && !degreeFilter.has(reducedSteps))
        ? "#000000"
        : this._getLumatoneHexColor(mappedCoords);
    entries.push({ board, key, hexColor });
  }
  return entries;
}

export function buildExquisColorArray() {
  const colors = new Array(61).fill("#000000");

  if (this.inputRuntime?.layoutMode === "sequential") {
    const scale = this.tuning.scale || [];
    const len = scale.length;
    if (len === 0) return colors;

    const anchorNote = this.settings.midiin_anchor_note ?? 60;
    const centerDegree = this.settings.center_degree || 0;

    for (let note = 0; note <= 60; note++) {
      let steps = note - anchorNote + centerDegree;
      let octs = Math.trunc(steps / len);
      let reducedSteps = steps % len;
      if (reducedSteps < 0) {
        reducedSteps += len;
        octs -= 1;
      }
      const cents = octs * this.tuning.equivInterval + scale[reducedSteps];
      if (reducedSteps === 0) {
        colors[note] = octs === 0 ? LUMATONE_TONIC : LUMATONE_TONIC_OTHER;
      } else {
        colors[note] = transferColor(this._getScreenHexColor(cents, reducedSteps));
      }
    }
    return colors;
  }

  for (const [mapKey, coords] of this.controllerMap) {
    const note = parseInt(mapKey.slice(mapKey.indexOf(".") + 1), 10);
    if (note >= 0 && note <= 60) {
      colors[note] = this._getLumatoneHexColor(controllerCoordsForDisplay.call(this, coords));
    }
  }
  return colors;
}

export function getLumatoneHexColor(coords) {
  const [cents, reducedSteps, , octs] = this.hexCoordsToCents(coords);

  if (reducedSteps === 0) {
    return octs === 0 ? LUMATONE_TONIC : LUMATONE_TONIC_OTHER;
  }

  const screenHex = this._getScreenHexColor(cents, reducedSteps);
  return transferColor(screenHex);
}

export function getScreenHexColor(cents, reducedSteps) {
  if (!this.settings.spectrum_colors) {
    const colors = this.settings.note_colors;
    if (!colors || typeof colors[reducedSteps] === "undefined") return "#edede4";
    return nameToHex(colors[reducedSteps]);
  }

  const fcolor = hex2rgb("#" + this.settings.fundamental_color);
  const hsv = rgb2hsv(fcolor[0], fcolor[1], fcolor[2]);
  let h = hsv.h / 360;
  const s = hsv.s / 100;
  const v = hsv.v / 100;
  let reduced = (cents / 1200) % 1;
  if (reduced < 0) reduced += 1;
  h = (reduced + h) % 1;
  const { red, green, blue } = HSVtoRGB2(h, s, v);
  return rgbToHex(red, green, blue);
}
